<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@sveltestrap/sveltestrap';

  import { csvStore } from '@client/stores/csv';
  import { streamingManager } from '@client/stores/streaming-manager';
  import { syncStore } from '@client/stores/sync';
  import { syncConnections } from '@client/stores/sync-connections';
  import { userStore } from '@client/stores/user';

  import { readFile } from '@client/utils/csv';

  import {
    SYNC_CONNECTIONS,
    SYNC_OPTIONS_FIELDS,
    SYNC_OPTIONS_MERGE,
    SYNC_OPTIONS_NOSYNC,
    SYNC_TYPES
  } from '@client/enums';

  import { UserSyncForm } from './shared';

  export let show = false;

  const defaultSyncOptions = $userStore?.featuresConfig?.defaultSyncOptions || {};

  let loading = false;
  let syncing = false;
  let fieldOptions = SYNC_OPTIONS_FIELDS.slice();
  let mergeOptions = SYNC_OPTIONS_MERGE.slice();
  let nosyncOptions = SYNC_OPTIONS_NOSYNC.slice();
  let sources = [];
  let types = [];

  let selectedUser = null;
  let user = '';

  const connectionsEnum = { ...SYNC_CONNECTIONS };

  $: isCSV = config.source.value === 'csv' || config.source.value === 'manual';

  // Disable if loading or if CSV and no file picked
  $: syncDisabled =
    loading ||
    (isCSV && (!$csvStore.parsed || $csvStore.parsed.errors?.length)) ||
    (config.type?.value === 'individual' && !user);

  const useDefaultSettings = () => ({
    ...defaultSyncOptions,
    defaultlocked: SYNC_OPTIONS_FIELDS[0],
    ignorenosync:
      defaultSyncOptions.ignorenosync !== undefined
        ? SYNC_OPTIONS_NOSYNC.find(({ value }) => value === defaultSyncOptions.ignorenosync)
        : false,
    replace:
      defaultSyncOptions.replace !== undefined
        ? SYNC_OPTIONS_MERGE.find(({ value }) => value === defaultSyncOptions.replace)
        : null,
    source: sources[0] || '',
    type: types[0] || '',
    files: []
  });

  let config = useDefaultSettings();

  const resetUserSelect = () => {
    selectedUser = null;
    user = '';
  };

  const onSourceChange = () => {
    reset();
    types = !isCSV && SYNC_TYPES.filter(({ value }) => connectionsEnum[config.source.value]?.supports.includes(value));

    if (types) {
      types = types.map((type) => ({ ...type, label: $format(type.label) }));

      config.type = types[0];
    }

    if (selectedUser) {
      resetUserSelect();
    }
  };

  const onTypeChange = () => {
    if (selectedUser) {
      resetUserSelect();
    }
  };

  const onMergeChange = () => {
    config.replace.label = $format(config.replace.label);
  };

  const onNoSyncChange = () => {
    config.ignorenosync.label = $format(config.ignorenosync.label);
  };

  const onDefaultLockedChange = () => {
    config.defaultlocked.label = $format(config.defaultlocked.label);
  };

  const onSync = async () => {
    const { source, type, files, defaultlocked, ignorenosync, replace, ...rest } = config;
    const endpoint = isCSV ? 'synccsv' : connectionsEnum[source.value].endpoint;
    const scope = isCSV ? '' : type?.value || '';

    let data = {
      syncOptions: {
        ...rest,
        replace: replace?.value !== undefined ? replace.value : null,
        defaultlocked: defaultlocked?.value,
        ignorenosync: ignorenosync?.value
      }
    };

    if (isCSV && $csvStore.parsed) {
      data.parsed = $csvStore.parsed;
    }

    if (scope === 'groups' || scope === 'organizations') {
      data = {};
    }

    // Get operation title for the streaming queue
    const getOperationTitle = () => {
      if (isCSV) return 'CSV Sync';
      if (scope === 'individual') return `Individual Sync - ${user}`;
      if (scope === 'groups') return 'Groups Sync';
      if (scope === 'organizations') return 'Organizations Sync';
      return `${source.label || 'Sync'} - ${type?.label || scope}`;
    };

    if (scope === 'individual') {
      const userData = {
        syncOptions: {
          ...data.syncOptions,
          googleQuery: user
        }
      };

      try {
        await streamingManager.startSyncOperation({
          title: getOperationTitle(),
          endpoint,
          scope: 'users', // Individual syncs always use 'users' scope
          payload: userData,
          total: null // Individual syncs don't have a known total
        });

        // Close modal after starting the operation
        show = false;
      } catch (error) {
        console.error('Failed to start individual sync:', error);
      }

      return;
    }

    // Additional validation for CSV operations
    if (isCSV && $csvStore.parsed?.errors?.length) {
      console.error('CSV has validation errors, cannot start sync');
      return;
    }

    try {
      await streamingManager.startSyncOperation({
        title: getOperationTitle(),
        endpoint,
        scope: isCSV ? '' : scope || 'users', // CSV operations use empty scope
        payload: data,
        total: isCSV ? $csvStore.parsed?.data?.length : null
      });

      // Close modal after starting the operation
      show = false;
    } catch (error) {
      console.error('Failed to start sync operation:', error);
      // Don't close modal if there was an error starting the operation
    }
  };

  const handleFileInput = async (file) => {
    const csvText = await readFile(file);
    csvStore.setCsv(csvText);
  };

  const reset = () => {
    csvStore.setCsv('');
  };

  const toggle = () => {
    config = useDefaultSettings();
    show = false;
  };

  const onSelectUsers = () => {
    user = selectedUser ? `email:${selectedUser.value}` : '';
  };

  // TODO add to an enum for reuse
  const csvOption = {
    label: 'label.UPLOAD_SPREADSHEET',
    value: 'csv',
    supports: ['users']
  };

  const manualCsvOption = {
    label: 'label.MANUAL_CSV',
    value: 'manual',
    supports: ['users']
  };

  // Available sync sources as label+value pairs for select:
  $: sources = $syncConnections
    ? [
        ...Object.keys($syncConnections)
          .filter((key) => $syncConnections[key])
          .map((key) => SYNC_CONNECTIONS[key])
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label)),
        csvOption,
        manualCsvOption
      ]
    : [];

  $: loading = !sources.length;

  $: if (sources.length && !config.source) {
    sources.forEach((source) => {
      source.label = $format(source.label);
    });

    config.source = sources[0];
    onSourceChange();
  }

  onMount(() => {
    reset();

    mergeOptions = mergeOptions.map((option) => ({
      ...option,
      label: $format(option.label)
    }));

    if (config.defaultlocked) {
      config.defaultlocked.label = $format(config.defaultlocked.label);
    }

    if (config.replace) {
      config.replace.label = $format(config.replace.label);
    }

    if (config.ignorenosync) {
      config.ignorenosync.label = $format(config.ignorenosync.label);
    }

    if (config.type) {
      config.type.label = $format(config.type.label);
    }
  });
</script>

<Modal id="user-sync-modal" bind:isOpen={show} size="md" {toggle}>
  <ModalHeader {toggle}>
    {$format('label.SYNC')}
  </ModalHeader>

  <ModalBody>
    <UserSyncForm
      bind:loading
      bind:sources
      bind:types
      bind:config
      bind:syncing
      bind:isCSV
      bind:selectedUser
      bind:fieldOptions
      bind:mergeOptions
      bind:nosyncOptions
      {onSourceChange}
      {onTypeChange}
      {onMergeChange}
      {onNoSyncChange}
      {onDefaultLockedChange}
      {onSelectUsers}
      {resetUserSelect}
      {handleFileInput}
      {reset}
    />
  </ModalBody>
  <ModalFooter>
    {#if config.source}
      <Button color="primary" disabled={syncDisabled} on:click={onSync}>{$format('label.START_SYNC')}</Button>
    {/if}
  </ModalFooter>
</Modal>

<style lang="scss" src="./sync.scss"></style>
